import { Component, Vue, Watch, PropSync } from 'vue-property-decorator';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';

import { ApiIdentifers } from '@/config/index';

import { AxDataService, FilterNewService } from '@/services';
import { LoggerService } from '@/utils';
import { FilterSidebarEventEnum } from '@/enums/FilterSidebarEventEnum';
import { ConfigurationSectionTypeEnum } from '@/enums/ConfigurationSectionTypeEnums';
import { FieldConfirmationTypeEnums } from '@/enums/FieldConfirmationTypeEnums';
import { ConfigurationFilterSection } from '@/models/filter/ConfigurationFilterSection';
import { ConfigurationFilterField } from '@/models/filter/ConfigurationFilterField';
import { DexieFilter } from '@/models/dexie/DexieFilter';
import { DexieFilterField } from '@/models/dexie/DexieFilterField';
import { FilterNameButtonEnum } from '@/enums/FilterNameButtonEnum';
import { UserProfileStore } from '@/store/store.userProfile';

import Section from '@/components/edit/section/Section.vue';
import FilterField from '@/components/filter/filterFieldNew/FilterField.vue';
import FilterSectionFields from '@/components/filter/filterSectionfields/FilterSectionfields.vue';
import FilterSidebar from '@/components/filter/filterSidebar/FilterSidebar.vue';
import ConfirmationCard from '@/components/general/confirmationMessage/ConfirmationCard.vue';
import ShareFilter from '@/components/filter/ShareFilter.vue';

@Component({
    components: {
        Section,
        ConfirmationCard,
        ShareFilter,
        FilterField,
        FilterSidebar,
        FilterSectionFields,
    },
})
export default class FilterComponent extends Vue {
    @PropSync('myFilter', { type: String }) public myfilter!: string;

    public sections: ConfigurationFilterSection[] = [];
    public selectedSectionData: ConfigurationFilterSection | null | undefined = null;
    public selectedSubSectionData: ConfigurationFilterField = new ConfigurationFilterField();
    public selectedItemLevelOne: any = null;
    public selectedItemLevelTwo: number = 0;
    public fieldTypes = FieldTypeEnum;

    public selectedSectionId: string = '';
    public selectedFilterNameText: string = '';
    public filterNameEditButton: FilterNameButtonEnum = FilterNameButtonEnum.ADD;
    public filterNameEditButtonTypes = FilterNameButtonEnum;
    public filterSelection: DexieFilter = new DexieFilter();

    public filters: DexieFilter[] = [];
    public selectedFilter: DexieFilter | undefined | null = null;
    public showEditFilterName: boolean = false;
    public filterChangeCounter: number = 0;
    public filterChangeExpandStrings: string[] = [];
    public filterHasUnsavedChanges: boolean = false;

    public showModal: boolean = false;
    public confirmed: boolean = false;
    public confirmationFrom: FieldConfirmationTypeEnums = FieldConfirmationTypeEnums.Unknown;
    public modalHeader: string = '';
    public modalBody: string = '';

    public share: boolean = false;
    public errorMessage: string = '';

    public toggleCounter: boolean = false;

    private ignoreSectionUpdate: boolean = false;
    private isInitialFetch: boolean = true;

    public async mounted() {
        this.resetShownFilterData();

        await AxDataService.GenerateMachineModelsForFilter();

        FilterNewService.OnFiltersUpdated.on(this.fetchFilters);
        this.fetchFilters();
    }

    public beforeDestroy(): void {
        FilterNewService.OnFiltersUpdated.off(this.fetchFilters);
    }

    public fetchFilters(): void {
        this.ignoreSectionUpdate = true;
        FilterNewService.getAllFilters()
            .then((fitlers) => {
                this.filters = fitlers;
            })
            .catch((error) => {
                LoggerService.error(`Error on getting all fitlers: `, error);
                this.filters = [];
            })
            .finally(() => {
                this.$nextTick(() => {
                    if (this.isInitialFetch === true) {
                        // if open a filter for edit
                        const usedFilter = FilterNewService.UsedFilterForDataSets;
                        if (usedFilter != null) {
                            /**
                             * check by name and id since unsynced filters have no uuid.
                             */
                            const toSelectFilter = this.filters.find((f) => f.filterName === usedFilter.filterName && f.id === usedFilter.id);
                            if (toSelectFilter != null && FilterNewService.UsedFilterForDataSetsHasUnsaved === false) {
                                // use filter if already exists and not edit
                                this.selectedFilter = toSelectFilter;
                                this.filterSelection = toSelectFilter;
                            } else {
                                this.filterSelection = usedFilter;
                            }

                            this.filterSelection.data.forEach((filterField) => {
                                this.setFilterCardData(filterField);
                            });

                            this.filterChangeCounter = this.counterLength();
                            this.selectedFilterNameText = this.filterSelection.filterName;
                        }
                        this.isInitialFetch = false;
                    }
                    this.$nextTick(() => {
                        this.ignoreSectionUpdate = false;
                        this.filterHasUnsavedChanges = FilterNewService.UsedFilterForDataSetsHasUnsaved;
                    });
                });
            });
    }

    public onSidebarEvent(inEvent: FilterSidebarEventEnum) {
        LoggerService.debug(`onSidebarEvent`, inEvent);
        switch (inEvent) {
            case FilterSidebarEventEnum.ADD:
                this.onAdd();
                break;
            case FilterSidebarEventEnum.APPLY:
                this.onApply();
                break;
            case FilterSidebarEventEnum.CLOSE:
                this.onClose();
                break;
            case FilterSidebarEventEnum.RELOAD_FILTER:
                this.ReloadFilter();
                break;
            case FilterSidebarEventEnum.REMOVE:
                this.confirmationMessage(FieldConfirmationTypeEnums.Remove);
                break;
            case FilterSidebarEventEnum.SAVE:
                if (!this.validateFilters()) {
                    this.confirmationMessage(FieldConfirmationTypeEnums.SpacesOnly);
                    return;
                } else {
                    this.confirmationMessage(FieldConfirmationTypeEnums.Save);
                }
                break;
            case FilterSidebarEventEnum.SHARE:
                this.onShare();
                break;

            default:
                break;
        }
    }

    @Watch('sections', { deep: true })
    public onSectionsChanged(newValue: ConfigurationFilterSection[]) {
        if (!this.ignoreSectionUpdate) {
            LoggerService.LogDebug(`onGetConfigurationSectionsChanged: `, newValue);
            this.setFilterData(newValue);
        }
    }

    /******** filter counter methods *****/
    /** Method called when filterCounter is expanded */
    public expandFilterCounter() {
        this.filterChangeExpandStrings = [this.$vuetify.lang.t('$vuetify.filter.selectedValuesExpand')];
        this.filterSelection.data.forEach((filter: DexieFilterField) => {
            let filterString = this.getIdentifierPathText(filter.identifierPath) + ' --> ';
            filterString += this.getFilterFieldValuesString(filter);
            this.filterChangeExpandStrings.push(filterString);
        });
    }

    public getFilterNameEditButtonText(): string {
        let name = '';
        switch (this.filterNameEditButton) {
            case FilterNameButtonEnum.EDIT:
                name = this.$vuetify.lang.t('$vuetify.filter.sidebar.edit');
                break;
            case FilterNameButtonEnum.ADD:
                name = this.$vuetify.lang.t('$vuetify.filter.sidebar.add');
                break;
            case FilterNameButtonEnum.SAVE:
                name = this.$vuetify.lang.t('$vuetify.filter.sidebar.save');
                break;

            default:
                break;
        }
        return name;
    }

    public manageToggleCounter() {
        this.toggleCounter = !this.toggleCounter;
        if (this.toggleCounter) {
            this.expandFilterCounter();
        }
    }

    public counterLength(): number {
        let count = 0;
        this.sections.forEach((section) => {
            count += this.getSetSectionValuesCounter(section);
        });
        return count;
    }

    /******* action methods *******/
    public onClose() {
        if (this.filterHasUnsavedChanges === true) {
            this.confirmationMessage(FieldConfirmationTypeEnums.Save);
        } else {
            this.$router.back();
        }
    }
    public closeModal() {
        this.showModal = false;
    }
    public discard() {
        this.$router.push('dashboard');
    }

    public async confirm(inType: FieldConfirmationTypeEnums) {
        this.showModal = false;
        this.confirmed = true;
        this.confirmationFrom = inType;
        switch (inType) {
            case FieldConfirmationTypeEnums.Save: {
                if (this.filterSelection.data.length === 0) {
                    this.confirmationMessage(FieldConfirmationTypeEnums.EmptyFilter);
                    break;
                } else {
                    this.onSave();
                    break;
                }
            }
            case FieldConfirmationTypeEnums.Remove: {
                this.onRemove();
                break;
            }
            case FieldConfirmationTypeEnums.Update: {
                this.onSave();
                break;
            }
            case FieldConfirmationTypeEnums.Apply: {
                this.onApply();
                break;
            }
            case FieldConfirmationTypeEnums.ApplyWithoutSave: {
                this.applyUnsavedFilter();
                break;
            }
            case FieldConfirmationTypeEnums.AddName: {
                break;
            }
            case FieldConfirmationTypeEnums.EmptyFilter: {
                break;
            }
            case FieldConfirmationTypeEnums.RemoveDefaultName: {
                break;
            }
            default: {
                break;
            }
        }
    }

    public onShare() {
        if (this.filterHasUnsavedChanges === true) {
            this.confirmationMessage(FieldConfirmationTypeEnums.Save);
        } else {
            this.share = true;
        }
    }

    public async onRemove() {
        if (this.selectedFilter != null) {
            FilterNewService.RemoveFilter(this.selectedFilter)
                .then(() => {
                    LoggerService.debug(`filter removed: ${this.selectedFilter ? this.selectedFilter.filterName : 'null'}`);
                    this.fetchFilters();
                    this.selectedFilter = null;
                    this.filterChangeCounter = this.counterLength();
                    this.resetFilterCardsData();
                })
                .catch((error) => {
                    LoggerService.error(`Error on removing filter: `, error);
                });
        }
    }

    public confirmationMessage(inType: FieldConfirmationTypeEnums) {
        this.showModal = true;
        switch (inType) {
            case FieldConfirmationTypeEnums.Apply: {
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.general', FieldConfirmationTypeEnums[inType]);
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.general', FieldConfirmationTypeEnums[inType]);
                break;
            }
            case FieldConfirmationTypeEnums.Save:
            case FieldConfirmationTypeEnums.Update:
            case FieldConfirmationTypeEnums.Remove: {
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.general', FieldConfirmationTypeEnums[inType]);
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.general', FieldConfirmationTypeEnums[inType]);
                break;
            }
            case FieldConfirmationTypeEnums.AddName: {
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.addName');
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.addName');
                break;
            }
            case FieldConfirmationTypeEnums.ApplyWithoutSave: {
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.unsavedFilter');
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.unsavedFilter');
                break;
            }
            case FieldConfirmationTypeEnums.EmptyFilter: {
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.emptyFilter');
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.emptyFilter');
                break;
            }
            case FieldConfirmationTypeEnums.Remove: {
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.addName');
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.removeDefaultName');
                break;
            }
            case FieldConfirmationTypeEnums.SpacesOnly: {
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.spacesOnly');
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.spacesOnly');
                break;
            }
            case FieldConfirmationTypeEnums.SpacesOnlyInName: {
                this.modalHeader = this.$vuetify.lang.t('$vuetify.filter.popupHeaders.spacesOnly');
                this.modalBody = this.$vuetify.lang.t('$vuetify.filter.popupBody.spacesOnlyInName');
                break;
            }
        }
        this.confirmationFrom = inType;
        if (this.confirmed === true) {
            return true;
        } else {
            return false;
        }
    }
    public async onApply() {
        this.expandFilterCounter();
        if (this.filterHasUnsavedChanges) {
            if (!this.validateFilters()) {
                this.confirmationMessage(FieldConfirmationTypeEnums.SpacesOnly);
            } else {
                this.confirmationMessage(FieldConfirmationTypeEnums.ApplyWithoutSave);
            }
        } else {
            FilterNewService.UsedFilterForDataSets = new DexieFilter(this.selectedFilter != null ? this.selectedFilter : this.filterSelection);
            FilterNewService.UsedFilterForDataSetFilters = this.filterChangeExpandStrings;
            FilterNewService.UsedFilterForDataSetsHasUnsaved = this.filterHasUnsavedChanges;
            this.$router.push({ name: 'table', query: { myFilter: 'true' } });
        }
    }

    public async applyUnsavedFilter() {
        this.filterSelection.isUnsaved = true;
        FilterNewService.UsedFilterForDataSets = new DexieFilter(this.filterSelection);
        FilterNewService.UsedFilterForDataSetFilters = this.filterChangeExpandStrings;
        FilterNewService.UsedFilterForDataSetsHasUnsaved = this.filterHasUnsavedChanges;
        this.$router.push({ name: 'table', query: { myFilter: 'true' } });
    }

    public async onSave() {
        if (this.selectedFilter != null) {
            /**
             * update an existing filter, if synched one, add to unsynched and
             * remove from synched till all filters synched again with BE
             */
            const addUpdate = (inFilter: DexieFilter): void => {
                FilterNewService.AddOrUpdateUnsyncedFilter(inFilter)
                    .then((response) => {
                        LoggerService.debug(`Unsynced filter updated, id: ${response}`);
                        this.filterHasUnsavedChanges = false;
                        FilterNewService.SyncFiltersWithServer().then(() => {
                            this.fetchFilters();
                        });
                    })
                    .catch((error) => {
                        LoggerService.error(`Error on updating unsynced filter: `, error);
                    });
            };

            if (this.filterSelection.isSynched === true) {
                FilterNewService.DeleteSyncedFilter(this.filterSelection)
                    .then(() => {
                        this.filterSelection.isSynched = false;
                        addUpdate(this.filterSelection);
                    })
                    .catch((error) => {
                        LoggerService.error(`Could not remove updated filter from synced fitlers: `, error);
                    });
            } else {
                addUpdate(this.filterSelection);
            }
        } else {
            // add a new filter
            if (this.filterSelection.filterName === '' || this.filterSelection.filterName.length === 0) {
                // need to add a name
                this.confirmationMessage(FieldConfirmationTypeEnums.AddName);
                return;
            } else {
                // save new unsynced filter
                this.filterSelection.userId = UserProfileStore.GetUserProfile.userId;
                FilterNewService.AddOrUpdateUnsyncedFilter(this.filterSelection)
                    .then((response) => {
                        LoggerService.debug(`Unsynced filter added, id: ${response}`);
                        this.filterHasUnsavedChanges = false;
                        FilterNewService.UsedFilterForDataSetsHasUnsaved = false;
                        FilterNewService.SyncFiltersWithServer().then(() => {
                            this.fetchFilters();
                        });
                    })
                    .catch((error) => {
                        LoggerService.error(`Error on adding unsynced filter: `, error);
                    });
            }
        }
    }

    public async onAdd() {
        if (this.selectedFilter != null) {
            this.resetShownFilterData();
            this.selectedFilter = undefined;
        }
        this.$nextTick(() => {
            if (this.filterHasUnsavedChanges === true) {
                this.confirmationMessage(FieldConfirmationTypeEnums.Save);
            } else {
                this.filterNameEditButton = FilterNameButtonEnum.SAVE;
                this.showEditFilterName = true;
            }
        });
    }

    /**
     * Clicking on the different Sections available (first column of filter)
     * @param sectionIndex
     */
    public onClick(inIdentifier: string) {
        this.selectedSectionId = '';

        this.selectedSubSectionData = new ConfigurationFilterField();
        this.selectedSectionData = this.sections.find((s) => s.identifier === inIdentifier);

        if (this.selectedSectionData) {
            const sectionFields = this.selectedSectionData.fields;
            this.selectedSectionId = this.selectedSectionData.identifier;
        }
    }

    public validateFieldName(): boolean {
        let valid = true;
        if (this.checkForSpacesOnly(this.selectedFilterNameText)) {
            valid = false;
            this.errorMessage = this.$vuetify.lang.t('$vuetify.filter.validate.textOnlySpace');
        } else if (this.selectedFilterNameText.length > 20) {
            this.errorMessage = this.$vuetify.lang.t('$vuetify.filter.validate.textToLong');
            valid = false;
        } else {
            this.errorMessage = '';
        }
        return valid;
    }

    public selectedSectionFields(inSection?: ConfigurationFilterSection): number {
        let length = 0;
        if (inSection && inSection.fields) {
            length = inSection.fields.length;
        }
        return length;
    }

    /**
     * Clicking on the subsections available when a section is selected
     * (second column of the filter)
     * @param subsectionIndex
     */
    public onClickSubsection(inIdentifier: string) {
        this.selectedSubSectionData = new ConfigurationFilterField();
        if (this.selectedSectionData) {
            const subSection = this.selectedSectionData.fields.find((f: ConfigurationFilterField) => f.identifier === inIdentifier);
            if (subSection) {
                this.selectedSubSectionData = subSection;
            }
        }
    }

    /***** Filter Related Methods  *******/

    public saveEditChangedFilterName(): void {
        if (this.filterNameEditButton === FilterNameButtonEnum.SAVE) {
            if (this.selectedFilterNameText.length === 0 || this.selectedFilterNameText === '') {
                this.confirmationMessage(FieldConfirmationTypeEnums.AddName);
                return;
            } else if (this.checkForSpacesOnly(this.selectedFilterNameText)) {
                this.confirmationMessage(FieldConfirmationTypeEnums.SpacesOnlyInName);
                return;
            }

            this.filterSelection.filterName = this.selectedFilterNameText;
            if (this.selectedFilter != null && this.selectedFilter.filterName !== this.selectedFilterNameText) {
                /**
                 * if change name of existing selected filter,
                 * change name of select value
                 */
                this.selectedFilter.filterName = this.selectedFilterNameText;
                this.filterNameEditButton = FilterNameButtonEnum.EDIT;
                this.showEditFilterName = false;
                // TODO: sync updated filter name
            } else if (this.selectedFilterNameText.length > 0) {
                this.filterNameEditButton = FilterNameButtonEnum.EDIT;
            }
        } else if (this.filterNameEditButton === FilterNameButtonEnum.EDIT || this.filterNameEditButton === FilterNameButtonEnum.ADD) {
            if (this.selectedFilter != null) {
                this.selectedFilterNameText = this.selectedFilter.filterName;
            }
            this.filterNameEditButton = FilterNameButtonEnum.SAVE;
            this.showEditFilterName = true;
        }
    }

    /**
     * Select a filter name from the dropdown of filter names
     * @param name
     */
    public selectFilter() {
        this.filterNameEditButton = FilterNameButtonEnum.EDIT;
        this.resetShownFilterData();

        if (this.selectedFilter) {
            this.filterSelection = new DexieFilter(this.selectedFilter);
            this.ignoreSectionUpdate = true;
            this.filterSelection.data.forEach((filterField) => {
                this.setFilterCardData(filterField);
            });
            this.$nextTick(() => {
                this.ignoreSectionUpdate = false;
            });
        }
    }

    /**
     * Method to reload filters. If savedFilters are null, refresh filter, Else load from saved.
     * @param name
     */
    public async ReloadFilter(name?: string, route?: boolean) {
        this.resetShownFilterData();

        if (this.selectedFilter != null) {
            this.ignoreSectionUpdate = true;
            this.selectedFilter.data.forEach((filterField) => {
                this.setFilterCardData(filterField);
            });
            this.filterChangeCounter = this.counterLength();
            this.filterHasUnsavedChanges = false;
            this.$nextTick(() => {
                this.ignoreSectionUpdate = false;
            });
        }
    }

    /***** Helper Functions  *******/
    /**
     * Method to fetch translation with fallback
     * @param inData ConfigurationFilterSection
     */
    public getIdentifierText(inData?: ConfigurationFilterSection | ConfigurationFilterField): string {
        let ret: string = '';
        if (inData) {
            ret = this.$vuetify.lang.t(`$vuetify.identifiers.${inData.identifier}`);
            // fallback to name string if no translation found
            if (ret === undefined || ret.length === 0 || ret === inData.identifier || ret.includes('$vuetify.identifiers.')) {
                ret = inData.name;
                LoggerService.debug(`getIdentifierText fallback text for: ${inData.identifier} --> ${ret}`, inData);
            }
        }
        return ret;
    }

    public getSetValuesCounter(inField: ConfigurationFilterField): number {
        let setValueCount = 0;
        if (inField.fields != null) {
            for (const subField of inField.fields) {
                setValueCount += subField.getSetValuesCounter();
            }
        } else {
            setValueCount += inField.getSetValuesCounter();
        }

        return setValueCount;
    }

    public getSetSectionValuesCounter(inSection: ConfigurationFilterSection): number {
        let setValueCount = 0;
        for (const field of inSection.fields) {
            setValueCount += field.getSetValuesCounter();
        }
        return setValueCount;
    }

    /********** Private Methods ***********/

    /**
     * Method to set/update/remove values from
     * fitler data array.
     *
     * @param inValues
     */
    private setFilterData(inValues: ConfigurationFilterSection[]) {
        // if(this.filterSelection == null) {
        //     this.filterSelection = new DexieFilter();
        // }
        this.sections.forEach((section) => {
            section.fields.forEach((field) => {
                this.setFilterFieldValues(field, this.filterSelection, [section.identifier, field.identifier]);
            });
        });
        this.filterChangeCounter = this.counterLength();
        this.filterHasUnsavedChanges = true;
        LoggerService.debug(`new this.filterSelection: `, this.filterSelection);
    }

    private setFilterFieldValues(inField: ConfigurationFilterField, inFilter: DexieFilter, inIdentifierPath: string[]) {
        if (inField.fields != null) {
            inField.fields.forEach((field) => {
                this.setFilterFieldValues(field, inFilter, [...inIdentifierPath, field.identifier]);
            });
        } else {
            const filterField = inFilter.data.find((item) => item.identifier === inField.identifier);
            if (filterField != null) {
                if (inField.hasNoValuesSet()) {
                    /**
                     * Field seems to be resetted --> delete from data
                     */
                    const filterFieldIndex = inFilter.data.findIndex((item) => item.identifier === inField.identifier);
                    if (filterFieldIndex >= 0) {
                        inFilter.data.splice(filterFieldIndex, 1);
                    }
                } else {
                    // update existing filter field
                    filterField.valueAXStrings = inField.valueAXStrings;
                    filterField.valueBool = inField.valueBool;
                    filterField.valueNumber = inField.valueNumber && inField.valueNumber.some((x) => !isNaN(x)) ? inField.valueNumber : undefined;
                    filterField.valueNumberTo = inField.valueNumberTo;
                    filterField.valueString = inField.valueString;
                    filterField.valueStringTo = inField.valueStringTo;
                }
            } else if (!inField.hasNoValuesSet()) {
                // add new filter
                const newFilterField = new DexieFilterField({
                    identifier: inField.identifier,
                    name: inField.name,
                    type: inField.type,
                    identifierPath: inIdentifierPath,
                    valueAXStrings: inField.valueAXStrings,
                    valueBool: inField.valueBool,
                    valueNumber: inField.valueNumber && inField.valueNumber.some((x) => !isNaN(x)) ? inField.valueNumber : undefined,
                    valueNumberTo: inField.valueNumberTo,
                    valueString: inField.valueString,
                    valueStringTo: inField.valueStringTo,
                });
                newFilterField.name = inField.name;
                inFilter.data.push(newFilterField);
            }
        }
    }

    private setFilterCardData(inFilter: DexieFilterField) {
        const setFilterSubCardData = (inSubFilter: DexieFilterField, inConfigurationFilterField: ConfigurationFilterField): boolean => {
            let wasSet = false;
            if (inConfigurationFilterField.fields != null) {
                for (const subField of inConfigurationFilterField.fields) {
                    wasSet = setFilterSubCardData(inSubFilter, subField);
                    if (wasSet === true) {
                        break;
                    }
                }
            } else if (inFilter.identifier === inConfigurationFilterField.identifier) {
                inConfigurationFilterField.valueAXStrings = inFilter.valueAXStrings;
                inConfigurationFilterField.valueBool = inFilter.valueBool;
                inConfigurationFilterField.valueNumber = inFilter.valueNumber;
                inConfigurationFilterField.valueNumberTo = inFilter.valueNumberTo;
                inConfigurationFilterField.valueString = inFilter.valueString;
                inConfigurationFilterField.valueStringTo = inFilter.valueStringTo;
                wasSet = true;
            }
            return wasSet;
        };

        for (const section of this.sections) {
            let wasSet = false;
            for (const sectionField of section.fields) {
                if (setFilterSubCardData(inFilter, sectionField) === true) {
                    wasSet = true;
                    break;
                }
            }
            if (wasSet === true) {
                break;
            }
        }

        this.filterHasUnsavedChanges = false;
        this.filterChangeCounter = this.counterLength();
    }

    private resetFilterCardsData() {
        this.sections = [];
        const sortBy = new ConfigurationFilterSection({
            name: 'Filter By DataSet',
            identifier: ApiIdentifers.filterSortByDataSet,
            position: 0,
            type: ConfigurationSectionTypeEnum.GENERAL,
            fields: [],
        });

        sortBy.fields.push(
            new ConfigurationFilterField({
                type: FieldTypeEnum.CHECKBOX,
                name: 'Data Sets created by Me',
                identifier: ApiIdentifers.filterDatasetsByMe,
                position: 1,
                required: false,
                isValid: true,
                collapsible: false,
            }),
            new ConfigurationFilterField({
                type: FieldTypeEnum.TEXT,
                name: 'Filter by Tab ID',
                identifier: ApiIdentifers.filterTabId,
                position: 2,
                required: false,
                isValid: true,
                collapsible: false,
            }),
            new ConfigurationFilterField({
                type: FieldTypeEnum.TEXT,
                name: 'Filter by Creator',
                identifier: ApiIdentifers.filterCreatedBy,
                position: 3,
                required: false,
                isValid: true,
                collapsible: false,
            }),
            new ConfigurationFilterField({
                type: FieldTypeEnum.TEXT,
                name: 'Filter by Editor',
                identifier: ApiIdentifers.filterEditedBy,
                position: 5,
                required: false,
                isValid: true,
                collapsible: false,
            }),
            new ConfigurationFilterField({
                type: FieldTypeEnum.FILTER_DATE,
                name: 'Filter by Creation Date',
                identifier: ApiIdentifers.filterCreatedAt,
                position: 4,
                required: false,
                isValid: true,
                collapsible: false,
            }),
            new ConfigurationFilterField({
                type: FieldTypeEnum.FILTER_DATE,
                name: 'Filter by Date Edited',
                identifier: ApiIdentifers.filterEditedAt,
                position: 6,
                required: false,
                isValid: true,
                collapsible: false,
            }),
        );

        // this.selectData.push({ text: 'Filter By DataSet', value: 'sortByCreator' });
        FilterNewService.generateUsedConfigurationFilterSections().forEach((section) => {
            this.sections.push(new ConfigurationFilterSection(section));
        });
        // add static sort by section in front
        this.sections.unshift(sortBy);
    }

    private getIdentifierPathText(identifierPath: string[]): string {
        let path = '';
        identifierPath.forEach((current) => {
            let identifierString: string = ' - ';
            identifierString = this.$vuetify.lang.t(`$vuetify.identifiers.${current}`);
            // fallback to name string if no translation found
            if (identifierString === undefined || identifierString.length === 0 || identifierString === current || identifierString.includes('$vuetify.identifiers.')) {
                identifierString = ' - ';
                LoggerService.debug(`getIdentifierPathText fallback text for: ${current} --> ${identifierString}`);
            }
            path += `${path.length > 0 ? ', ' : ''}${identifierString}`;
        });
        return path;
    }

    private getFilterFieldValuesString(inField: DexieFilterField): string {
        let values = '';
        switch (inField.type) {
            case FieldTypeEnum.CHECKBOX:
                values = inField.valueBool ? 'true' : 'false';
                break;
            case FieldTypeEnum.DATE:
            case FieldTypeEnum.FILTER_DATE:
                values = (inField.valueString && inField.valueString.substring(0, 10)) || '';
                values += values.length > 0 && inField.valueStringTo != null ? ', ' : '';
                values += (inField.valueStringTo && inField.valueStringTo.substring(0, 10)) || '';
                break;
            case FieldTypeEnum.NUMBER:
                values = inField.valueNumber != null ? inField.valueNumber.join(', ') : '';
                values += values.length > 0 && inField.valueNumberTo != null ? ', ' : '';
                values += inField.valueNumberTo != null ? inField.valueNumberTo.toString() : '';
                break;
            case FieldTypeEnum.TEXT:
            case FieldTypeEnum.TEXTAREA:
                values = inField.valueString ? (inField.valueString.length > 13 ? inField.valueString.slice(0, 12) + '...' : inField.valueString) : '';
                break;
            case FieldTypeEnum.SELECT:
                values = this.getSelectValuesString(inField);
                break;
            case FieldTypeEnum.AXSELECT:
                values = inField.valueAXStrings ? inField.valueAXStrings.map((f) => f.text).join(', ') : '';
                break;
            default:
                break;
        }

        return values;
    }

    private getSelectValuesString(inField: DexieFilterField): string {
        let values = '';
        const checkSubField = (inSubField: ConfigurationFilterField): boolean => {
            let found = false;
            if (inSubField.fields) {
                for (const subField of inSubField.fields) {
                    const subFound = checkSubField(subField);
                    if (subFound) {
                        found = true;
                        break;
                    }
                }
            } else if (inSubField.identifier === inField.identifier) {
                if (inSubField.values) {
                    values = inSubField.values
                        .filter((f) => inField.valueNumber && inField.valueNumber.some((v) => v === f.value))
                        .map((x) => x.text)
                        .join(', ');
                }
                found = true;
            }

            return found;
        };

        for (const section of this.sections) {
            let foundField = false;
            for (const field of section.fields) {
                foundField = checkSubField(field);
                if (foundField) {
                    break;
                }
            }
            if (foundField) {
                break;
            }
        }
        return values;
    }

    private resetShownFilterData() {
        this.filterHasUnsavedChanges = false;
        this.ignoreSectionUpdate = true;
        this.toggleCounter = false;
        this.filterChangeExpandStrings = [this.$vuetify.lang.t('$vuetify.filter.selectedValuesExpand')];
        this.resetFilterCardsData();
        this.selectedSectionData = undefined;
        this.selectedSubSectionData = new ConfigurationFilterField();
        this.selectedItemLevelOne = undefined;
        this.filterSelection = new DexieFilter();
        this.selectedSectionId = '';
        this.selectedFilterNameText = '';
        this.filterNameEditButton = FilterNameButtonEnum.ADD;
        this.filterChangeCounter = this.counterLength();
        this.$nextTick(() => {
            this.ignoreSectionUpdate = false;
        });
    }

    private validateFilters(): boolean {
        let allFilterValid = true;

        for (const filterField of this.filterSelection.data) {
            if (filterField.type === FieldTypeEnum.TEXT || filterField.type === FieldTypeEnum.TEXTAREA) {
                if (filterField.valueString != null && this.checkForSpacesOnly(filterField.valueString)) {
                    allFilterValid = false;
                    break;
                }
            }
        }

        return allFilterValid;
    }

    private checkForSpacesOnly(inString: string): boolean {
        return /^\s+$/.test(inString);
    }
}
